import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AgentShowcase = () => {
  const navigate = useNavigate();

  const agents = [
    {
      id: 'survey-scraper',
      name: 'SurveyExtractor',
      icon: 'Download',
      description: 'Automatically extracts and extracts survey data from multiple public sources including Ipsos databases, ONS, and UNData for comprehensive market research analysis.',
      features: ['Data Extraction', 'Multi-Source Extraction', 'Format Standardization', 'Quality Validation'],
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-950/20',
      iconColor: 'text-blue-600 dark:text-blue-400'
    },
    {
      id: 'insight-analyzer',
      name: 'InsightAnalyzer',
      icon: 'Search',
      description: 'Deep-dive analysis agent that processes extracted survey data to identify trends, patterns, and correlations for actionable market intelligence and client reporting.',
      features: ['Pattern Recognition', 'Trend Analysis', 'Statistical Modeling', 'Insight Generation'],
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50 dark:bg-purple-950/20',
      iconColor: 'text-purple-600 dark:text-purple-400'
    },
    {
      id: 'projection-engine',
      name: 'ProjectionEngine',
      icon: 'TrendingUp',
      description: 'Generates accurate earnings projections and market forecasts for clients based on analyzed survey data and historical trends to support strategic decision-making.',
      features: ['Earnings Forecasting', 'Market Projections', 'Risk Assessment', 'Client Reporting'],
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50 dark:bg-emerald-950/20',
      iconColor: 'text-emerald-600 dark:text-emerald-400'
    }
  ];

  const handleLearnMore = (agentId) => {
    navigate('/launch-agent', { state: { selectedAgent: agentId } });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const workflowSteps = [
    {
      icon: 'UserCheck',
      title: 'Individual Employee Login',
      description: 'Each Ipsos employee has secure individual access credentials to track their work progress and maintain accountability.',
      color: 'text-blue-600 dark:text-blue-400'
    },
    {
      icon: 'FileText',
      title: 'Work Documentation',
      description: 'All research activities, data processing, and analysis are automatically documented and logged within the employee\'s workspace.',
      color: 'text-purple-600 dark:text-purple-400'
    },
    {
      icon: 'Eye',
      title: 'Human Intelligence Review',
      description: 'Dedicated HI reviewers examine all documented work to ensure accuracy, compliance, and quality standards before final approval.',
      color: 'text-orange-600 dark:text-orange-400'
    },
    {
      icon: 'CheckCircle',
      title: 'System Submission',
      description: 'Only after Human Intelligence approval can research data and insights be officially submitted to the central system and client reporting.',
      color: 'text-emerald-600 dark:text-emerald-400'
    }
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Specialized AI Agents
          </h2>
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto">
            Purpose-built AI agents for Ipsos professionals to automate survey data collection, analysis, and client projection workflows
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
        >
          {agents?.map((agent, index) => (
            <motion.div
              key={agent?.id}
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              className={`relative group ${agent?.bgColor} rounded-2xl p-8 border border-border/50 backdrop-blur-sm hover:shadow-2xl transition-all duration-500`}
            >
              {/* Animated background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${agent?.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
              
              {/* Icon with animation */}
              <div className="relative mb-6">
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                  className={`w-16 h-16 ${agent?.bgColor} rounded-xl flex items-center justify-center mb-4 border border-border/30`}
                >
                  <Icon 
                    name={agent?.icon} 
                    size={32} 
                    className={agent?.iconColor}
                  />
                </motion.div>
                
                {/* Floating particles around icon */}
                <div className="absolute -top-2 -right-2">
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.3,
                    }}
                    className="w-3 h-3 bg-accent rounded-full"
                  />
                </div>
              </div>

              <div className="relative">
                <h3 className="text-2xl font-bold text-foreground mb-4">
                  {agent?.name}
                </h3>
                
                <p className="text-text-secondary mb-6 leading-relaxed">
                  {agent?.description}
                </p>

                {/* Features list */}
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                    Key Features
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {agent?.features?.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-accent rounded-full"></div>
                        <span className="text-sm text-text-secondary">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleLearnMore(agent?.id)}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="w-full group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-all duration-300"
                >
                  Launch {agent?.name}
                </Button>
              </div>

              {/* Hover glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${agent?.color} opacity-0 group-hover:opacity-10 rounded-2xl blur-xl transition-opacity duration-500 -z-10`}></div>
            </motion.div>
          ))}
        </motion.div>

        {/* Employee Workflow Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-card rounded-3xl p-8 md:p-12 border border-border shadow-lg mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Employee Workflow & Quality Assurance
            </h3>
            <p className="text-lg text-text-secondary max-w-3xl mx-auto">
              Our comprehensive employee tracking and Human Intelligence review system ensures every piece of research maintains the highest quality standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflowSteps?.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4 border border-border">
                    <Icon 
                      name={step?.icon} 
                      size={28} 
                      className={step?.color}
                    />
                  </div>
                  {index < workflowSteps?.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-border to-transparent transform -translate-x-8"></div>
                  )}
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">{index + 1}</span>
                  </div>
                </div>
                
                <h4 className="text-lg font-semibold text-foreground mb-3">
                  {step?.title}
                </h4>
                
                <p className="text-text-secondary text-sm leading-relaxed">
                  {step?.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Key Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 pt-8 border-t border-border"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Icon name="Shield" size={24} className="text-primary mb-2" />
                <span className="text-sm font-medium text-foreground">100% Quality Controlled</span>
              </div>
              <div className="flex flex-col items-center">
                <Icon name="Users" size={24} className="text-primary mb-2" />
                <span className="text-sm font-medium text-foreground">Individual Accountability</span>
              </div>
              <div className="flex flex-col items-center">
                <Icon name="CheckCircle2" size={24} className="text-primary mb-2" />
                <span className="text-sm font-medium text-foreground">HI Approved Submissions</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <Button
            variant="default"
            size="lg"
            onClick={() => navigate('/launch-agent')}
            iconName="Zap"
            iconPosition="left"
            className="px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
          >
            Launch AI Agent
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default AgentShowcase;